import { createStore } from 'jotai'
import { createContext, useContext } from 'react'
import type { SafeZone } from './atoms'

export const CanvasStoreContext = createContext<ReturnType<typeof createStore> | null>(null)

export function useCanvasStore() {
	const store = useContext(CanvasStoreContext)
	if (!store) {
		throw new Error('CanvasStoreContext not found')
	}
	return store
}

/**
 * Safe zone flows through React context — NOT a jotai atom — on purpose.
 *
 * The canvas root's width/height are committed in the same render as the
 * safe-zone prop change. If the safe zone reached the content layers via an
 * atom synced in a useEffect, their margin styles would mutate one flush
 * *after* the canvas resize. Chrome ≥150 has a layout-invalidation regression
 * where a margin change on an absolutely-positioned element, applied after its
 * containing block was resized in an earlier frame, updates computed style but
 * never triggers relayout — the layer visibly keeps its stale geometry until
 * an unrelated layout pass touches it. Context updates pierce memo() and
 * commit in the same DOM mutation batch as the resize, which avoids the bug.
 */
export const SafeZoneContext = createContext<SafeZone>({ top: 0, right: 0, bottom: 0, left: 0 })

export function useSafeZone(): SafeZone {
	return useContext(SafeZoneContext)
}
